import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, User, GraduationCap, Camera,
    PenTool, CreditCard, Car, FileText, Upload, X, Check, Shield,
    Image, BookOpen, Award, Plus
} from 'lucide-react';

const DocumentVault = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [uploadedDocs, setUploadedDocs] = useState({});
    const fileInputRef = useRef(null);
    const [activeField, setActiveField] = useState(null);
    const [customDocuments, setCustomDocuments] = useState({});
    const [showAddCustom, setShowAddCustom] = useState(false);
    const [newCustomDocName, setNewCustomDocName] = useState('');

    // Document structure for each page
    const documentPages = [
        {
            title: "Personal Identity Documents",
            icon: User,
            description: "Upload your identity proofs and personal documents",
            fields: [
                { id: 'passport_photo', label: 'Passport Size Photo', icon: Camera, required: true },
                { id: 'signature', label: 'Signature', icon: PenTool, required: true },
                { id: 'aadhaar', label: 'Aadhaar Card', icon: CreditCard, required: true },
                { id: 'pan', label: 'PAN Card', icon: CreditCard, required: false },
                { id: 'driving_licence', label: 'Driving Licence', icon: Car, required: false }
            ]
        },
        {
            title: "Educational & Academic Documents",
            icon: GraduationCap,
            description: "Upload your educational certificates and marksheets",
            fields: [
                { id: '10th_marksheet', label: '10th Marksheet', icon: BookOpen, required: true },
                { id: '12th_marksheet', label: '12th Marksheet', icon: BookOpen, required: true },
                { id: 'graduation_marksheet', label: 'Graduation Marksheet', icon: Award, required: false }
            ]
        }
    ];

    const currentPage = documentPages[currentStep - 1];

    const handleFileUpload = (fieldId, event) => {
        const file = event.target.files[0];
        if (file) {
            const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
            setUploadedDocs(prev => ({
                ...prev,
                [fieldId]: {
                    file: file,
                    name: file.name,
                    preview: previewUrl,
                    size: (file.size / 1024).toFixed(1) + ' KB'
                }
            }));
        }
        setActiveField(null);
    };

    const triggerFileUpload = (fieldId) => {
        setActiveField(fieldId);
        fileInputRef.current?.click();
    };

    const removeDocument = (fieldId) => {
        setUploadedDocs(prev => {
            const updated = { ...prev };
            if (updated[fieldId]?.preview) {
                URL.revokeObjectURL(updated[fieldId].preview);
            }
            delete updated[fieldId];
            return updated;
        });
    };

    const addCustomDocument = () => {
        if (!newCustomDocName.trim() || !currentPage) return;
        const customDoc = {
            id: `custom_${Date.now()}`,
            label: newCustomDocName.trim(),
            icon: FileText,
            isCustom: true
        };
        setCustomDocuments(prev => ({
            ...prev,
            [currentPage.title]: [...(prev[currentPage.title] || []), customDoc]
        }));
        setNewCustomDocName('');
        setShowAddCustom(false);
    };

    const removeCustomDocument = (docId) => {
        setCustomDocuments(prev => ({
            ...prev,
            [currentPage.title]: (prev[currentPage.title] || []).filter(d => d.id !== docId)
        }));
        removeDocument(docId);
    };

    const handleSave = () => {
        console.log('Saving documents:', uploadedDocs);
        // Here you would typically upload to server
        navigate('/docs');
    };

    // Progress calculation
    const getProgress = () => {
        const allFields = documentPages.flatMap(page => page.fields.map(f => f.id));
        const uploadedCount = allFields.filter(id => uploadedDocs[id]).length;
        return Math.round((uploadedCount / allFields.length) * 100);
    };

    const totalPages = documentPages.length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
            <div className="max-w-3xl mx-auto">

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => activeField && handleFileUpload(activeField, e)}
                />

                {/* Header */}
                <div className="text-center mb-4">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Upload Documents</h1>
                </div>

                {/* Step Indicators - Like Signup Pages */}
                <div className="flex gap-4 mb-6 px-2">
                    {documentPages.map((page, index) => {
                        const stepNum = index + 1;
                        const isActive = currentStep === stepNum;
                        const isPast = currentStep > stepNum;
                        const PageIcon = page.icon;

                        return (
                            <div key={index} className="flex flex-col gap-1.5 flex-1">
                                <div
                                    className={`h-1.5 rounded-full transition-all duration-500 ${isPast ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
                                            : isActive ? 'bg-blue-400'
                                                : 'bg-gray-200'
                                        }`}
                                />
                                <div className="flex items-center gap-1.5">
                                    <PageIcon className={`w-3 h-3 ${isActive ? 'text-blue-600' : isPast ? 'text-green-600' : 'text-gray-400'
                                        }`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? 'text-blue-600' : isPast ? 'text-green-600' : 'text-gray-400'
                                        }`}>
                                        {page.title.split(' ')[0]}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 p-6 border border-white/60 backdrop-blur-sm">
                    {/* Page Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-xl">
                                <currentPage.icon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{currentPage.title}</h3>
                                <p className="text-[10px] text-gray-500 font-medium">
                                    Page {currentStep} of {totalPages}
                                </p>
                            </div>
                        </div>

                        {/* Pagination Dots */}
                        <div className="flex gap-2">
                            {documentPages.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentStep(index + 1)}
                                    className={`h-2 rounded-full transition-all duration-300 ${currentStep === index + 1 ? 'bg-blue-600 w-6' : 'bg-gray-200 w-2 hover:bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Document Upload Fields */}
                    <div className="space-y-1.5 min-h-[300px]">
                        {currentPage.fields.map((field) => {
                            const isUploaded = uploadedDocs[field.id];
                            const FieldIcon = field.icon;

                            return (
                                <div
                                    key={field.id}
                                    className={`flex items-center justify-between p-2 rounded-md border transition-all ${isUploaded
                                            ? 'border-green-200 bg-green-50'
                                            : 'border-gray-100 hover:border-gray-200 bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded-md ${isUploaded ? 'bg-green-100' : 'bg-white'} flex items-center justify-center`}>
                                            {isUploaded ? (
                                                <Check className="w-3 h-3 text-green-500" />
                                            ) : (
                                                <FieldIcon className="w-3 h-3 text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 text-[11px]">
                                                {field.label}
                                                {field.required && <span className="text-red-500 ml-0.5">*</span>}
                                            </p>
                                            {isUploaded && (
                                                <p className="text-[9px] text-green-600">{uploadedDocs[field.id].name}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                        {isUploaded ? (
                                            <button
                                                onClick={() => removeDocument(field.id)}
                                                className="p-1 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => triggerFileUpload(field.id)}
                                                className="flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-md text-[11px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                            >
                                                <Upload className="w-3 h-3" />
                                                Upload
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Custom Documents for current page */}
                        {(customDocuments[currentPage.title] || []).map((doc) => {
                            const isUploaded = uploadedDocs[doc.id];

                            return (
                                <div
                                    key={doc.id}
                                    className={`flex items-center justify-between p-2 rounded-md border transition-all ${isUploaded
                                            ? 'border-green-200 bg-green-50'
                                            : 'border-dashed border-gray-200 bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded-md ${isUploaded ? 'bg-green-100' : 'bg-white'} flex items-center justify-center`}>
                                            {isUploaded ? (
                                                <Check className="w-3 h-3 text-green-500" />
                                            ) : (
                                                <FileText className="w-3 h-3 text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 text-[11px]">{doc.label}</p>
                                            <span className="text-[9px] text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded-full">Custom</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                        {isUploaded ? (
                                            <button
                                                onClick={() => removeDocument(doc.id)}
                                                className="p-1 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => triggerFileUpload(doc.id)}
                                                    className="flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-md text-[11px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                                >
                                                    <Upload className="w-3 h-3" />
                                                    Upload
                                                </button>
                                                <button
                                                    onClick={() => removeCustomDocument(doc.id)}
                                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Add Custom Document */}
                        {showAddCustom ? (
                            <div className="flex items-center gap-1.5 p-2 rounded-md border border-dashed border-blue-300 bg-blue-50">
                                <input
                                    type="text"
                                    value={newCustomDocName}
                                    onChange={(e) => setNewCustomDocName(e.target.value)}
                                    placeholder="Enter document name"
                                    className="flex-1 px-2.5 py-1 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-[11px]"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomDocument();
                                        if (e.key === 'Escape') setShowAddCustom(false);
                                    }}
                                />
                                <button onClick={addCustomDocument} className="p-1 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                    <Check className="w-3 h-3" />
                                </button>
                                <button onClick={() => { setShowAddCustom(false); setNewCustomDocName(''); }} className="p-1 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowAddCustom(true)}
                                className="w-full flex items-center justify-center gap-1.5 p-2 rounded-md border border-dashed border-gray-200 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all"
                            >
                                <Plus className="w-3 h-3" />
                                <span className="font-medium text-[11px]">Add Custom Document</span>
                            </button>
                        )}
                    </div>

                    {/* Navigation Buttons - Like Signup Pages */}
                    <div className="mt-6 pt-4 border-t border-gray-50 flex justify-between gap-4">
                        {currentStep > 1 ? (
                            <button
                                onClick={() => setCurrentStep(prev => prev - 1)}
                                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-100 text-gray-600 font-bold hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-[0.98] text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/docs')}
                                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-100 text-gray-600 font-bold hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-[0.98] text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}

                        {currentStep < totalPages ? (
                            <button
                                onClick={() => setCurrentStep(prev => prev + 1)}
                                className="flex-[1.5] flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] text-sm"
                            >
                                Continue
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSave}
                                className="flex-[1.5] bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-2xl font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-600/20 active:scale-[0.98] text-sm flex items-center justify-center gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Save All Documents
                            </button>
                        )}
                    </div>
                </div>

                {/* Security Footer */}
                <p className="mt-4 text-center text-[10px] text-gray-400 font-medium">
                    Your data is secure and encrypted. Need help? <span className="text-blue-500 cursor-pointer hover:underline">Contact Support</span>
                </p>
            </div>
        </div>
    );
};

export default DocumentVault;
